import { Button, Center, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IconFigmaArrowRight, IconFigmaShieldCheck } from '@/assets/icons';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';
import { AuthInfoBox } from '@/features/auth/components/AuthInfoBox';
import { formatRole } from '@/utils/format';

interface RegistrationSuccessCardProps {
  orgName: string;
  firstName: string;
  lastName: string;
  email: string;
  partnerOrgName: string;
}

export const RegistrationSuccessCard = ({
  orgName,
  firstName,
  lastName,
  email,
  partnerOrgName
}: RegistrationSuccessCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const orgRows: [string, string][] = [
    [t('verifyEmail.successOrgLabel'), orgName],
    [t('verifyEmail.successStatusLabel'), t('platformAdmin.statusActive')]
  ];
  const firstUserRows: [string, string][] = [
    [t('verifyEmail.successNameLabel'), `${firstName} ${lastName}`],
    [t('verifyEmail.successEmailLabel'), email],
    [t('verifyEmail.successRoleLabel'), formatRole('ORGANISATION_ADMIN')]
  ];
  const connectedRows: [string, string][] = [
    [t('connect.successPartnerLabel'), partnerOrgName],
    [t('connect.successStatusLabel'), t('connect.successStatusActive')]
  ];

  return (
    <AuthCard px={32} py={36} w={{ base: '100%', md: 440 }}>
      <Stack align="center" gap={14}>
        <Center
          w={56}
          h={56}
          style={{
            borderRadius: 12,
            backgroundColor: 'var(--color-accent-secondary-soft)',
            color: 'var(--color-icon-accent)'
          }}
        >
          <IconFigmaShieldCheck size={24} />
        </Center>
        <Title order={2} fz={24} fw={700} ta="center" style={brandTextStyle}>
          {t('connect.registrationSuccessTitle')}
        </Title>
        <Text size="sm" ta="center" style={{ color: 'var(--color-text-secondary)' }}>
          {t('verifyEmail.successMessage')}
        </Text>

        <AuthInfoBox rows={orgRows} />

        <Text fz={12} fw={600} w="100%" style={{ color: 'var(--color-text-secondary)' }}>
          {t('verifyEmail.successFirstUserLabel')}
        </Text>
        <AuthInfoBox rows={firstUserRows} />

        <Text fz={12} fw={600} w="100%" style={{ color: 'var(--color-text-secondary)' }}>
          {t('connect.registrationSuccessConnectedLabel')}
        </Text>
        <AuthInfoBox rows={connectedRows} />

        <Button
          fullWidth
          rightSection={<IconFigmaArrowRight size={15} />}
          onClick={() => navigate('/login')}
        >
          {t('connect.registrationSuccessButton')}
        </Button>
      </Stack>
    </AuthCard>
  );
};
